import React, { useState, useEffect, useCallback } from 'react';
import { debounce } from '../utils';

/**
 * 주문내역 등에서 열 수 있는 모달.
 * 탭 1: 거래설정종목 (AI 설정 종목 + AI 분석 제외 종목)
 * 탭 2: 금지종목 (거래/매수/매도 금지) — 검색으로 종목 추가 후 즉시 저장
 */
const TradeSettingsSymbolsModal = ({ onClose, authenticatedFetch, user, onUserUpdated }) => {
  const [activeTab, setActiveTab] = useState('config'); // 'config' | 'blocked'
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 금지종목 탭: 검색 및 옵션
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [blockOption, setBlockOption] = useState('both'); // 'both' | 'buy' | 'sell' — 기본 매도+매수(거래 금지)
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [selectedSearchSymbol, setSelectedSearchSymbol] = useState(null); // { symbol, name } from search result
  const [removingSymbol, setRemovingSymbol] = useState(null); // symbol being removed (for loading state)

  useEffect(() => {
    if (!authenticatedFetch) {
      setError('인증 함수가 제공되지 않았습니다.');
      setLoading(false);
      return;
    }
    fetchConfig();
  }, [authenticatedFetch]);

  // 주문 메뉴와 동일한 종목 검색 (디바운스 300ms)
  const runSearch = useCallback(
    (keyword) => {
      if (!keyword || keyword.trim().length < 1) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      const trimmedKeyword = keyword.trim();

      if (/^\d{6}$/.test(trimmedKeyword)) {
        setIsSearching(true);
        authenticatedFetch(`/api/trading/stock/${trimmedKeyword}`, { timeout: 30000 })
          .then((response) => {
            if (response.ok) {
              return response.json().then((stockData) => {
                const result = {
                  symbol: stockData.symbol || trimmedKeyword,
                  name: stockData.name || stockData.stock_name || '종목명 조회 중...',
                  stock_name: stockData.stock_name || stockData.name || '종목명 조회 중...',
                  market: stockData.market || 'KOSPI/KOSDAQ',
                  price: stockData.price || 0
                };
                setSearchResults([result]);
                setShowSearchResults(true);
              });
            }
            return authenticatedFetch(`/api/trading/search?keyword=${encodeURIComponent(trimmedKeyword)}`, { timeout: 30000 }).then((searchResponse) => {
              if (searchResponse.ok) return searchResponse.json().then((data) => { setSearchResults(data.results || []); setShowSearchResults(true); });
              setSearchResults([]);
              setShowSearchResults(true);
            });
          })
          .catch((err) => {
            console.warn('종목 검색 실패:', err);
            authenticatedFetch(`/api/trading/search?keyword=${encodeURIComponent(trimmedKeyword)}`, { timeout: 30000 })
              .then((searchResponse) => {
                if (searchResponse.ok) return searchResponse.json().then((data) => { setSearchResults(data.results || []); setShowSearchResults(true); });
                setSearchResults([]);
                setShowSearchResults(true);
              })
              .catch(() => { setSearchResults([]); setShowSearchResults(true); });
          })
          .finally(() => setIsSearching(false));
        return;
      }

      if (trimmedKeyword.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      authenticatedFetch(`/api/trading/search?keyword=${encodeURIComponent(trimmedKeyword)}`, { timeout: 30000 })
        .then((response) => {
          if (response.ok) return response.json().then((data) => { setSearchResults(data.results || []); setShowSearchResults(true); });
          setSearchResults([]);
          setShowSearchResults(true);
        })
        .catch((err) => {
          console.warn('종목 검색 실패:', err);
          setSearchResults([]);
          setShowSearchResults(true);
        })
        .finally(() => setIsSearching(false));
    },
    [authenticatedFetch]
  );

  const searchStock = useCallback(
    debounce((keyword) => runSearch(keyword), 300),
    [runSearch]
  );

  useEffect(() => {
    if (searchKeyword) {
      searchStock(searchKeyword);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchKeyword, searchStock]);

  const applyBlockedSymbol = async () => {
    if (!selectedSearchSymbol || !selectedSearchSymbol.symbol || !authenticatedFetch || !user) return;
    const symbol = (selectedSearchSymbol.symbol || '').toString().trim();
    if (!symbol) return;

    const userTrading = user.trading || {};
    let blocked = Array.isArray(userTrading.blocked_symbols) ? [...userTrading.blocked_symbols] : [];
    let blockedBuy = Array.isArray(userTrading.blocked_buy_symbols) ? [...userTrading.blocked_buy_symbols] : [];
    let blockedSell = Array.isArray(userTrading.blocked_sell_symbols) ? [...userTrading.blocked_sell_symbols] : [];

    const addIfMissing = (arr, s) => (arr.includes(s) ? arr : [...arr, s]);

    if (blockOption === 'both') {
      blocked = addIfMissing(blocked, symbol);
      blockedBuy = blockedBuy.filter((s) => s !== symbol);
      blockedSell = blockedSell.filter((s) => s !== symbol);
    } else if (blockOption === 'buy') {
      blockedBuy = addIfMissing(blockedBuy, symbol);
      blocked = blocked.filter((s) => s !== symbol);
    } else {
      blockedSell = addIfMissing(blockedSell, symbol);
      blocked = blocked.filter((s) => s !== symbol);
    }

    setApplyLoading(true);
    setApplyError(null);
    try {
      const requestData = {
        nickname: user.nickname || '',
        name: user.name || '',
        trading: {
          ...userTrading,
          blocked_symbols: blocked,
          blocked_buy_symbols: blockedBuy,
          blocked_sell_symbols: blockedSell
        }
      };
      const response = await authenticatedFetch('/api/user/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
        timeout: 30000
      });
      if (response.ok) {
        const updatedUser = { ...user, trading: { ...user.trading, blocked_symbols: blocked, blocked_buy_symbols: blockedBuy, blocked_sell_symbols: blockedSell } };
        if (onUserUpdated) await onUserUpdated(true, updatedUser);
        setSelectedSearchSymbol(null);
        setSearchKeyword('');
        setShowSearchResults(false);
        setSearchResults([]);
      } else {
        const errData = await response.json().catch(() => ({}));
        setApplyError(errData.error || `저장 실패 (${response.status})`);
      }
    } catch (err) {
      console.error('금지종목 저장 오류:', err);
      setApplyError(err.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setApplyLoading(false);
    }
  };

  const removeBlockedSymbol = async (symbol, type) => {
    if (!symbol || !authenticatedFetch || !user) return;
    const sym = (symbol || '').toString().trim();
    if (!sym) return;

    const userTrading = user.trading || {};
    let blocked = Array.isArray(userTrading.blocked_symbols) ? [...userTrading.blocked_symbols] : [];
    let blockedBuy = Array.isArray(userTrading.blocked_buy_symbols) ? [...userTrading.blocked_buy_symbols] : [];
    let blockedSell = Array.isArray(userTrading.blocked_sell_symbols) ? [...userTrading.blocked_sell_symbols] : [];

    if (type === 'both') {
      blocked = blocked.filter((s) => s !== sym);
    } else if (type === 'buy') {
      blockedBuy = blockedBuy.filter((s) => s !== sym);
    } else {
      blockedSell = blockedSell.filter((s) => s !== sym);
    }

    setRemovingSymbol(sym);
    setApplyError(null);
    try {
      const requestData = {
        nickname: user.nickname || '',
        name: user.name || '',
        trading: {
          ...userTrading,
          blocked_symbols: blocked,
          blocked_buy_symbols: blockedBuy,
          blocked_sell_symbols: blockedSell
        }
      };
      const response = await authenticatedFetch('/api/user/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
        timeout: 30000
      });
      if (response.ok) {
        const updatedUser = { ...user, trading: { ...user.trading, blocked_symbols: blocked, blocked_buy_symbols: blockedBuy, blocked_sell_symbols: blockedSell } };
        if (onUserUpdated) await onUserUpdated(true, updatedUser);
      } else {
        const errData = await response.json().catch(() => ({}));
        setApplyError(errData.error || `해제 실패 (${response.status})`);
      }
    } catch (err) {
      console.error('금지종목 해제 오류:', err);
      setApplyError(err.message || '해제 중 오류가 발생했습니다.');
    } finally {
      setRemovingSymbol(null);
    }
  };

  const fetchConfig = async () => {
    if (!authenticatedFetch) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch('/api/ai-trade/config', { timeout: 30000 });
      if (response.ok) {
        const data = await response.json();
        setConfig(data.config);
      } else if (response.status === 404) {
        setConfig(null); // 설정 없음도 유효
      } else {
        setError(`설정을 불러오는데 실패했습니다. (${response.status})`);
      }
    } catch (err) {
      console.error('거래설정 조회 오류:', err);
      setError(err.message?.includes('401') ? '인증이 필요합니다.' : '설정을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const metadata = config?.metadata instanceof Map
    ? Object.fromEntries(config.metadata)
    : (config?.metadata || {});
  const symbolConfigs = Array.isArray(config?.symbol_configs) ? config.symbol_configs : [];
  const excludedSymbols = Array.isArray(metadata.excluded_symbols) ? metadata.excluded_symbols : [];

  const userTrading = user?.trading || {};
  const blockedSymbols = Array.isArray(userTrading.blocked_symbols) ? userTrading.blocked_symbols : [];
  const blockedBuySymbols = Array.isArray(userTrading.blocked_buy_symbols) ? userTrading.blocked_buy_symbols : [];
  const blockedSellSymbols = Array.isArray(userTrading.blocked_sell_symbols) ? userTrading.blocked_sell_symbols : [];

  const listStyle = {
    listStyle: 'none',
    padding: 0,
    margin: '8px 0 0 0',
    maxHeight: '280px',
    overflowY: 'auto',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '12px'
  };
  const itemStyle = {
    padding: '8px 12px',
    marginBottom: '4px',
    backgroundColor: '#fff',
    borderRadius: '6px',
    fontSize: '14px',
    border: '1px solid #eee'
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '520px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #e0e0e0', paddingBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>📋 거래설정 · 금지종목</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#f44336',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={() => setActiveTab('config')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: activeTab === 'config' ? '#667eea' : '#f0f0f0',
              color: activeTab === 'config' ? '#fff' : '#333'
            }}
          >
            📌 거래설정종목
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('blocked')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: activeTab === 'blocked' ? '#f57c00' : '#f0f0f0',
              color: activeTab === 'blocked' ? '#fff' : '#333'
            }}
          >
            🚫 금지종목
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '24px' }}>불러오는 중...</p>
        ) : error ? (
          <p style={{ color: '#d32f2f', padding: '12px', backgroundColor: '#ffebee', borderRadius: '8px' }}>{error}</p>
        ) : activeTab === 'config' ? (
          <>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
              AI 거래 설정에서 관리하는 종목입니다. <strong>AI 거래 설정</strong> 탭에서 수정할 수 있습니다.
            </p>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#333' }}>AI 설정 종목 ({symbolConfigs.length}개)</h3>
              {symbolConfigs.length === 0 ? (
                <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>등록된 종목이 없습니다.</p>
              ) : (
                <ul style={listStyle}>
                  {symbolConfigs.map((item, i) => (
                    <li key={item.symbol || i} style={itemStyle}>
                      {item.symbol || item.code || '-'}
                      {item.name ? ` (${item.name})` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#333' }}>AI 분석 제외 종목 ({excludedSymbols.length}개)</h3>
              {excludedSymbols.length === 0 ? (
                <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>없음</p>
              ) : (
                <ul style={listStyle}>
                  {excludedSymbols.map((s, i) => (
                    <li key={typeof s === 'string' ? s : i} style={itemStyle}>{typeof s === 'string' ? s : String(s)}</li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
              AI 자동거래 시 매수/매도에서 제외되는 종목입니다. 검색 후 옵션을 선택하고 적용하면 즉시 저장됩니다.
            </p>

            {/* 검색 블록: 주문 메뉴와 동일한 검색 + 옵션 + 적용 */}
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee' }}>
              <div style={{ marginBottom: '8px' }}>
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  placeholder="종목명 또는 종목코드 검색 (예: 삼성전자, 005930)"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
                />
              </div>
              {isSearching && <span style={{ fontSize: '12px', color: '#666' }}>검색 중...</span>}
              {showSearchResults && searchResults.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0 0', maxHeight: '160px', overflowY: 'auto', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #eee' }}>
                  {searchResults.map((stock, index) => {
                    const sym = (stock.symbol || '').toString().trim();
                    const isSelected = selectedSearchSymbol && selectedSearchSymbol.symbol === sym;
                    return (
                      <li
                        key={sym || index}
                        onClick={() => setSelectedSearchSymbol({ symbol: sym, name: stock.name || stock.stock_name || '' })}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: index < searchResults.length - 1 ? '1px solid #eee' : 'none',
                          backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                          borderRadius: '4px'
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{sym}</span>
                        {stock.name || stock.stock_name ? ` - ${stock.name || stock.stock_name}` : ''}
                      </li>
                    );
                  })}
                </ul>
              )}
              {showSearchResults && searchKeyword && searchResults.length === 0 && !isSearching && (
                <div style={{ padding: '8px 0', fontSize: '13px', color: '#999' }}>검색 결과가 없습니다.</div>
              )}
              <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#333', marginRight: '4px' }}>금지 유형:</span>
                {[
                  { value: 'both', label: '매도+매수 (거래 금지)' },
                  { value: 'buy', label: '매수 금지' },
                  { value: 'sell', label: '매도 금지' }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBlockOption(opt.value)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      backgroundColor: blockOption === opt.value ? '#f57c00' : '#fff',
                      color: blockOption === opt.value ? '#fff' : '#333'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={applyBlockedSymbol}
                  disabled={!selectedSearchSymbol || applyLoading}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: selectedSearchSymbol && !applyLoading ? '#667eea' : '#ccc',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: selectedSearchSymbol && !applyLoading ? 'pointer' : 'not-allowed',
                    fontSize: '13px',
                    fontWeight: 600
                  }}
                >
                  {applyLoading ? '저장 중...' : '적용'}
                </button>
              </div>
            </div>

            {/* 등록 종목 리스트: 옵션(금지 유형) + 해제 버튼 */}
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#333' }}>
                등록 종목 리스트 ({blockedSymbols.length + blockedBuySymbols.length + blockedSellSymbols.length}개)
              </h3>
              {blockedSymbols.length === 0 && blockedBuySymbols.length === 0 && blockedSellSymbols.length === 0 ? (
                <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>등록된 금지종목이 없습니다. 위에서 검색 후 적용하세요.</p>
              ) : (
                <ul style={listStyle}>
                  {blockedSymbols.map((s, i) => (
                    <li key={`both-${s}-${i}`} style={{ ...itemStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                      <span><strong>{s}</strong></span>
                      <span style={{ fontSize: '12px', color: '#d32f2f', fontWeight: 600 }}>거래 금지</span>
                      <button
                        type="button"
                        onClick={() => removeBlockedSymbol(s, 'both')}
                        disabled={removingSymbol === s}
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          backgroundColor: '#fff',
                          color: '#d32f2f',
                          border: '1px solid #d32f2f',
                          borderRadius: '6px',
                          cursor: removingSymbol === s ? 'not-allowed' : 'pointer',
                          opacity: removingSymbol === s ? 0.7 : 1
                        }}
                      >
                        {removingSymbol === s ? '해제 중...' : '해제'}
                      </button>
                    </li>
                  ))}
                  {blockedBuySymbols.map((s, i) => (
                    <li key={`buy-${s}-${i}`} style={{ ...itemStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                      <span><strong>{s}</strong></span>
                      <span style={{ fontSize: '12px', color: '#f57c00', fontWeight: 600 }}>매수 금지</span>
                      <button
                        type="button"
                        onClick={() => removeBlockedSymbol(s, 'buy')}
                        disabled={removingSymbol === s}
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          backgroundColor: '#fff',
                          color: '#f57c00',
                          border: '1px solid #f57c00',
                          borderRadius: '6px',
                          cursor: removingSymbol === s ? 'not-allowed' : 'pointer',
                          opacity: removingSymbol === s ? 0.7 : 1
                        }}
                      >
                        {removingSymbol === s ? '해제 중...' : '해제'}
                      </button>
                    </li>
                  ))}
                  {blockedSellSymbols.map((s, i) => (
                    <li key={`sell-${s}-${i}`} style={{ ...itemStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                      <span><strong>{s}</strong></span>
                      <span style={{ fontSize: '12px', color: '#1976d2', fontWeight: 600 }}>매도 금지</span>
                      <button
                        type="button"
                        onClick={() => removeBlockedSymbol(s, 'sell')}
                        disabled={removingSymbol === s}
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          backgroundColor: '#fff',
                          color: '#1976d2',
                          border: '1px solid #1976d2',
                          borderRadius: '6px',
                          cursor: removingSymbol === s ? 'not-allowed' : 'pointer',
                          opacity: removingSymbol === s ? 0.7 : 1
                        }}
                      >
                        {removingSymbol === s ? '해제 중...' : '해제'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {applyError && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#d32f2f' }}>{applyError}</div>
              )}
            </div>
          </>
        )}

        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '10px 24px',
              backgroundColor: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradeSettingsSymbolsModal;
